import Image from 'next/image'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { MentorSpotlight } from './mentor-carousel'

import type {
  NonFsdsGeneralV1MentorSpotlight,
  NonFsdsGeneralV1StatItem,
} from '@/types/non-fsds-general-v1.type'

interface ValueMentorSectionProps {
  title: string
  description?: string
  stats: NonFsdsGeneralV1StatItem[]
  mentors: NonFsdsGeneralV1MentorSpotlight[]
  experienceDescription?: string
  experienceKeywords?: string[]
  isEnglish: boolean
  className?: string
}

/**
 * General V1 Value/Mentor composite — Figma node 1:1552.
 *
 * Top row: Value/stats panel left + Mentor spotlight right.
 * Bottom row: Learning experience description left + keyword list right.
 * Stats embed the four approved icon assets at 80px.
 */
export function ValueMentorSection({
  title,
  description,
  stats,
  mentors,
  experienceDescription,
  experienceKeywords,
  isEnglish,
  className,
}: ValueMentorSectionProps) {
  const visibleKeywords = experienceKeywords?.slice(0, 4) ?? []
  const [activeKeyword, setActiveKeyword] = React.useState(() =>
    Math.min(2, Math.max(visibleKeywords.length - 1, 0))
  )

  React.useEffect(() => {
    if (activeKeyword < visibleKeywords.length) return
    setActiveKeyword(Math.max(visibleKeywords.length - 1, 0))
  }, [activeKeyword, visibleKeywords.length])

  return (
    <section
      className={cn('w-full bg-[var(--gv1-bg-surface)] py-20', className)}
      data-testid="gv1-value-mentor"
    >
      <div className="gv1-container">
        {/* Top row: Stats + Mentor spotlight */}
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-[7fr_5fr]">
          {/* Value/Stats panel */}
          <div className="flex min-h-[416px] flex-col rounded-[var(--gv1-radius-card)] border border-slate-200/70 bg-white p-6">
            <div>
              <h2
                className="text-2xl font-bold text-[var(--gv1-text-heading)]"
                style={{ fontFamily: 'var(--gv1-font-heading)' }}
              >
                {title}
              </h2>
              {description && (
                <p className="mt-4 max-w-[650px] text-[15px] leading-6 text-[var(--gv1-text-body)]">
                  {description}
                </p>
              )}
            </div>

            <div className="mt-auto grid grid-cols-2 gap-5 sm:grid-cols-4">
              {stats.slice(0, 4).map((stat, idx) => (
                <div key={idx} className="flex flex-col items-center text-center">
                  <div className="relative mb-3 flex size-20 items-center justify-center rounded-2xl bg-slate-50">
                    {stat.icon && (
                      <Image
                        src={stat.icon}
                        alt=""
                        width={48}
                        height={48}
                        className="size-12 object-contain"
                        aria-hidden="true"
                      />
                    )}
                    <span className="absolute -right-1 -top-2 rounded-full bg-[var(--gv1-accent)] px-2 py-0.5 text-xs font-bold text-white">
                      {stat.value}
                    </span>
                  </div>
                  <div
                    className="text-sm font-medium text-[var(--gv1-accent)]"
                    style={{ fontFamily: 'var(--gv1-font-body)' }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mentor spotlight */}
          <MentorSpotlight mentors={mentors} isEnglish={isEnglish} />
        </div>

        {/* Bottom row: Learning Experience */}
        {(experienceDescription || (experienceKeywords && experienceKeywords.length > 0)) && (
          <div className="mt-2 grid min-h-[186px] grid-cols-1 gap-2 rounded-[var(--gv1-radius-card)] border border-slate-200/70 bg-white p-6 lg:grid-cols-[7fr_5fr]">
            {/* Description */}
            {experienceDescription && (
              <div className="flex items-start">
                <p
                  className="max-w-[650px] text-[15px] leading-6 text-[var(--gv1-text-body)]"
                  style={{ fontFamily: 'var(--gv1-font-body)' }}
                >
                  {experienceDescription}
                </p>
              </div>
            )}

            {/* Keyword list */}
            {visibleKeywords.length > 0 && (
              <div className="flex flex-col justify-center gap-0">
                {visibleKeywords.map((kw, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveKeyword(idx)}
                    onMouseEnter={() => setActiveKeyword(idx)}
                    aria-pressed={idx === activeKeyword}
                    className={cn(
                      'flex items-center gap-3 px-0 py-1 text-left text-[24px] font-medium leading-[26px] transition-colors duration-200',
                      idx === activeKeyword
                        ? 'text-[var(--gv1-text-heading)]'
                        : 'text-slate-900/20 hover:text-slate-900/50'
                    )}
                    style={{ fontFamily: 'var(--gv1-font-heading)' }}
                  >
                    {idx === activeKeyword && (
                      <svg className="size-4 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                      </svg>
                    )}
                    {kw}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
